import { dlog } from './debug';
// Emotion Engine - Calculates emotional states based on user behavior and match outcomes

import { 
  clusterUserColors, 
  getMostUsedColorFamily, 
  getFamilyEmotions,
  findColorFamily,
  hexToHsl
} from './colorClustering';
import { checkMatchCompletion, calculateSetsWon } from './matchCompletion';

/**
 * Core Emotion States (6 primary, scalable to 12)
 */
export const EMOTIONS = {
  JOYFUL: 'joyful',           // Recent wins, high streak
  CALM: 'calm',               // Steady play, neutral outcomes
  FOCUSED: 'focused',         // High match count, active
  COMPETITIVE: 'competitive', // Frequent close matches
  REFLECTIVE: 'reflective',   // No recent games, idle
  RESILIENT: 'resilient',     // Recovering from losses
};

/**
 * Progressive emotional range thresholds
 * Expands emotional range as more matches provide better data
 */
export const EMOTION_THRESHOLDS = {
  MIN_MATCHES_BASIC: 3,        // 2-4 matches: Basic range (joyful/competitive only)
  MIN_MATCHES_NUANCED: 8,      // 5-9 matches: Expanded range (+resilient)
  MIN_MATCHES_FULL: 15,        // 10+ matches: Full range (+reflective)
};

/**
 * Emotion Color Palette (HSL)
 */
export const EMOTION_COLORS = {
  [EMOTIONS.JOYFUL]: { h: 45, s: 100, l: 65 },    // Bright warm gold
  [EMOTIONS.CALM]: { h: 200, s: 80, l: 60 },      // Vibrant blue
  [EMOTIONS.FOCUSED]: { h: 180, s: 85, l: 55 },   // Bright cyan
  [EMOTIONS.COMPETITIVE]: { h: 15, s: 100, l: 60 }, // Bright coral
  [EMOTIONS.REFLECTIVE]: { h: 280, s: 70, l: 65 }, // Vibrant lavender
  [EMOTIONS.RESILIENT]: { h: 270, s: 85, l: 60 }, // Bright purple
};

/**
 * Get emotion color as HSL string
 */
export function getEmotionColor(emotion, saturation = 1.0) {
  const baseColor = EMOTION_COLORS[emotion] || EMOTION_COLORS[EMOTIONS.CALM];
  return `hsl(${baseColor.h}, ${Math.round(baseColor.s * saturation)}%, ${baseColor.l}%)`;
}

/**
 * Get most frequently used team color from matches using color clustering
 * This handles UGC color variations by grouping similar colors
 * @param {Array} matches - Array of match objects
 * @param {String} myTeam - Optional team name to filter colors for (only get colors from this team's matches)
 */
export function getMostUsedColor(matches, myTeam = null) {
  dlog('🎨 getMostUsedColor called with:', { matches: matches?.length, myTeam });
  
  if (!matches || matches.length === 0) {
    dlog('🎨 No matches provided, returning default');
    return '#C4B5A0'; // Default beige
  }

  dlog('🎨 getMostUsedColor called with myTeam:', myTeam);
  dlog('🎨 Available matches:', matches.map(m => ({
    id: m.id,
    teamA: typeof m.teamA === 'object' ? m.teamA.name : m.teamA,
    teamB: typeof m.teamB === 'object' ? m.teamB.name : m.teamB,
    teamAColor: m.teamAColor || (m.teamA && m.teamA.color),
    teamBColor: m.teamBColor || (m.teamB && m.teamB.color)
  })));

  // Helper to normalize team names (same logic as inferMyTeam)
  const normalizeTeamName = (teamName) => {
    if (!teamName) return teamName;
    return teamName
      .replace(/\s+\d+\s+\w+$/i, '') // Remove "16 Black", "2024 Gold", etc.
      .replace(/\s+\d+$/i, '') // Remove trailing numbers like "2024"
      .trim();
  };

  // Extract all colors from matches with metadata for tie-breaking
  const colorData = [];
  matches.forEach(match => {
    const matchDate = match.createdAt || match.updatedAt || Date.now();
    
    // Get team names (handle both string and object formats)
    const teamAName = typeof match.teamA === 'object' ? match.teamA.name || match.teamA.teamName : match.teamA;
    const teamBName = typeof match.teamB === 'object' ? match.teamB.name || match.teamB.teamName : match.teamB;
    
    // Normalize team names for comparison
    const normalizedTeamA = normalizeTeamName(teamAName);
    const normalizedTeamB = normalizeTeamName(teamBName);
    
    // If myTeam is specified, only include colors from matches where myTeam played
    if (myTeam) {
      const isTeamA = normalizedTeamA === myTeam;
      const isTeamB = normalizedTeamB === myTeam;
      
      // Only add colors from the side where myTeam played
      if (isTeamA) {
        // Add Team A's color only
        if (match.teamAColor) {
          colorData.push({ color: match.teamAColor, date: matchDate, matchId: match.id, team: teamAName });
        }
        if (match.teamA && typeof match.teamA === 'object' && match.teamA.color) {
          colorData.push({ color: match.teamA.color, date: matchDate, matchId: match.id, team: teamAName });
        }
      } else if (isTeamB) {
        // Add Team B's color only
        if (match.teamBColor) {
          colorData.push({ color: match.teamBColor, date: matchDate, matchId: match.id, team: teamBName });
        }
        if (match.teamB && typeof match.teamB === 'object' && match.teamB.color) {
          colorData.push({ color: match.teamB.color, date: matchDate, matchId: match.id, team: teamBName });
        }
      }
      // If myTeam didn't play in this match, skip it entirely
    } else {
      // No team filter - include all colors (original behavior)
      if (match.teamAColor) {
        colorData.push({ color: match.teamAColor, date: matchDate, matchId: match.id, team: teamAName });
      }
      if (match.teamBColor) {
        colorData.push({ color: match.teamBColor, date: matchDate, matchId: match.id, team: teamBName });
      }
      
      // Check if colors are stored in team objects
      if (match.teamA && typeof match.teamA === 'object' && match.teamA.color) {
        colorData.push({ color: match.teamA.color, date: matchDate, matchId: match.id, team: teamAName });
      }
      if (match.teamB && typeof match.teamB === 'object' && match.teamB.color) {
        colorData.push({ color: match.teamB.color, date: matchDate, matchId: match.id, team: teamBName });
      }
    }
  });
  
  // Convert to simple array for existing logic, but keep metadata for tie-breaking
  const allColors = colorData.map(item => item.color);
  
  dlog('🎨 Color data found:', colorData);
  dlog('🎨 All colors found:', allColors);

  if (myTeam) {
    dlog(`🎨 Color Debug - Filtered colors for ${myTeam}:`, allColors);
    dlog(`🎨 Color sources:`, colorData.map(item => ({ color: item.color, team: item.team })));
  } else {
    dlog('🎨 Color Debug - All Colors (no filter):', allColors);
  }
  
  // Dynamic color clustering: Group similar colors automatically
  const colorGroups = [];
  const processedColors = new Set();
  
  // For each color, find all similar colors and group them together
  allColors.forEach(color => {
    if (processedColors.has(color)) return;
    
    const hsl = hexToHsl(color);
    const group = {
      colors: [color],
      representativeColor: color,
      totalCount: 1
    };
    
    // Find all similar colors to this one
    allColors.forEach(otherColor => {
      if (processedColors.has(otherColor)) return;
      
      const otherHsl = hexToHsl(otherColor);
      
      // Calculate similarity (hue, saturation, lightness)
      let hueDiff = Math.abs(hsl.h - otherHsl.h);
      if (hueDiff > 180) hueDiff = 360 - hueDiff; // Handle wraparound
      
      const satDiff = Math.abs(hsl.s - otherHsl.s);
      const lightDiff = Math.abs(hsl.l - otherHsl.l);
      
      // Similarity thresholds (adjustable)
      const hueThreshold = 40; // Degrees
      const satThreshold = 40; // Percentage
      const lightThreshold = 30; // Percentage
      
      // Check if colors are similar
      const isSimilar = hueDiff <= hueThreshold && 
                       satDiff <= satThreshold && 
                       lightDiff <= lightThreshold;
      
      if (isSimilar) {
        group.colors.push(otherColor);
        group.totalCount++;
        processedColors.add(otherColor);
      }
    });
    
    processedColors.add(color);
    colorGroups.push(group);
  });
  
  dlog('🎨 Dynamic color groups detected:');
  colorGroups.forEach((group, index) => {
    if (group.colors.length > 0) {
      dlog(`  Group ${index + 1}: ${group.totalCount} colors`, group.colors);
    }
  });
  
  // Find the group with the most colors
  let largestGroup = null;
  let maxCount = 0;
  colorGroups.forEach(group => {
    if (group.totalCount > maxCount) {
      maxCount = group.totalCount;
      largestGroup = group;
    }
  });
  
  if (largestGroup && largestGroup.colors.length > 0) {
    // Return the most frequently used color from the largest group
    // If tied, use the most recent color (date created tiebreaker)
    const colorCounts = {};
    const colorDates = {};
    
    largestGroup.colors.forEach(color => {
      colorCounts[color] = (colorCounts[color] || 0) + 1;
      
      // Find the most recent date for this color
      colorData.forEach(item => {
        if (item.color === color) {
          if (!colorDates[color] || item.date > colorDates[color]) {
            colorDates[color] = item.date;
          }
        }
      });
    });
    
    // Find color with highest count, then use most recent date as tiebreaker
    let bestColor = null;
    let bestCount = 0;
    let bestDate = 0;
    
    Object.entries(colorCounts).forEach(([color, count]) => {
      const date = colorDates[color] || 0;
      
      if (count > bestCount || 
          (count === bestCount && date > bestDate)) {
        bestColor = color;
        bestCount = count;
        bestDate = date;
      }
    });
    
    dlog('🎨 Most frequent color from largest group:', bestColor, 'used', bestCount, 'times, most recent date:', new Date(bestDate).toISOString());
    return bestColor;
  }
  
  // Fallback: return most frequent of any color
  const colorCounts = {};
  allColors.forEach(color => {
    colorCounts[color] = (colorCounts[color] || 0) + 1;
  });
  
  const colorEntries = Object.entries(colorCounts);
  if (colorEntries.length === 0) {
    dlog('🎨 No colors found, returning default');
    return '#C4B5A0'; // Default beige
  }
  
  const mostFrequent = colorEntries.reduce((prev, current) => {
    return current[1] > prev[1] ? current : prev;
  }, colorEntries[0]); // Use first entry as initial value
  
  dlog('🎨 Most frequent color (fallback):', mostFrequent[0], 'used', mostFrequent[1], 'times');
  return mostFrequent[0];
}

/**
 * Get the color family for the most used color from matches
 * This is used for identity emotion calculation
 */
export function getMostUsedColorFamilyFromMatches(matches) {
  if (!matches || matches.length === 0) {
    return null;
  }

  // Extract all colors from matches
  const allColors = [];
  matches.forEach(match => {
    // Check direct color properties
    if (match.teamAColor) {
      allColors.push(match.teamAColor);
    }
    if (match.teamBColor) {
      allColors.push(match.teamBColor);
    }
    
    // Check if colors are stored in team objects
    if (match.teamA && typeof match.teamA === 'object' && match.teamA.color) {
      allColors.push(match.teamA.color);
    }
    if (match.teamB && typeof match.teamB === 'object' && match.teamB.color) {
      allColors.push(match.teamB.color);
    }
  });

  // Cluster similar colors and get most used family
  const clusters = clusterUserColors(allColors);
  return getMostUsedColorFamily(clusters);
}

/**
 * Calculate identity emotion based on user's consistent team patterns
 * Uses color family clustering to determine personality traits
 */
export function calculateIdentityEmotion(matches) {
  if (!matches || matches.length === 0) {
    return EMOTIONS.CALM;
  }

  // Progressive emotional range expansion for identity emotions
  if (matches.length < EMOTION_THRESHOLDS.MIN_MATCHES_BASIC) {
    // 1-2 matches: Default to focused (showing activity/engagement)
    dlog('📊 Using minimal identity range (1-2 matches) - defaulting to focused');
    return EMOTIONS.FOCUSED; // Active engagement with limited data
  }

  // Get the most used color family
  const colorFamily = getMostUsedColorFamilyFromMatches(matches);
  
  if (colorFamily && colorFamily.emotions) {
    // Use the first emotion from the color family's associated emotions
    const familyEmotion = colorFamily.emotions[0];
    
    // Map family emotions to our emotion constants
    const emotionMap = {
      'joyful': EMOTIONS.JOYFUL,
      'calm': EMOTIONS.CALM,
      'focused': EMOTIONS.FOCUSED,
      'competitive': EMOTIONS.COMPETITIVE,
      'reflective': EMOTIONS.REFLECTIVE,
      'resilient': EMOTIONS.RESILIENT,
      'excited': EMOTIONS.JOYFUL, // Map excited to joyful
      'determined': EMOTIONS.FOCUSED, // Map determined to focused
      'proud': EMOTIONS.JOYFUL // Map proud to joyful
    };
    
    const mappedEmotion = emotionMap[familyEmotion] || EMOTIONS.CALM;
    
    // Progressive range filtering based on match count
    if (matches.length < EMOTION_THRESHOLDS.MIN_MATCHES_NUANCED) {
      // 3-7 matches: Basic range only (joyful, calm, focused, competitive)
      if (mappedEmotion === EMOTIONS.RESILIENT || mappedEmotion === EMOTIONS.REFLECTIVE) {
        dlog('📊 Limited identity range (3-7 matches) - defaulting to focused');
        return EMOTIONS.FOCUSED;
      }
    } else if (matches.length < EMOTION_THRESHOLDS.MIN_MATCHES_FULL) {
      // 8-14 matches: Expanded range (adds resilient)
      if (mappedEmotion === EMOTIONS.REFLECTIVE) {
        dlog('📊 Expanded identity range (8-14 matches) - defaulting to resilient');
        return EMOTIONS.RESILIENT;
      }
    }
    // 15+ matches: Full range (all emotions available)
    
    return mappedEmotion;
  }

  // Fallback based on match count
  if (matches.length < EMOTION_THRESHOLDS.MIN_MATCHES_NUANCED) {
    return EMOTIONS.FOCUSED; // Active engagement
  } else {
    return EMOTIONS.CALM; // Steady state with more data
  }
}

/**
 * Calculate performance emotion and stats based on win/loss patterns
 * Returns both the emotion and the stats used for tooltip generation
 */
export function calculatePerformanceEmotionWithStats(matches) {
  if (!matches || matches.length === 0) {
    return { emotion: EMOTIONS.CALM, stats: { myTeam: null, wins: 0, losses: 0, totalGames: 0, winRate: 0 } };
  }

  // Debug: Show what team data looks like
  dlog('🔍 Team Data Debug:', matches.map(m => ({ 
    teamA: m.teamA, 
    teamB: m.teamB, 
    teamAType: typeof m.teamA,
    teamBType: typeof m.teamB,
    teamAColor: m.teamAColor || (m.teamA && m.teamA.color),
    teamBColor: m.teamBColor || (m.teamB && m.teamB.color),
    score: m.score,
    setsWon: m.setsWon,
    sets: m.sets,
    hasSets: !!m.sets
  })));

  // Infer "my team" based on most frequently used team name
  const myTeam = inferMyTeam(matches);
  dlog('🏆 My Team Inference:', { myTeam, totalMatches: matches.length });
  if (!myTeam) {
    return { emotion: EMOTIONS.CALM, stats: { myTeam: null, wins: 0, losses: 0, totalGames: 0, winRate: 0 } };
  }

  // Calculate win/loss ratio for my team (volleyball rules - by sets won)
  let wins = 0;
  let totalGames = 0;

  // Helper to normalize team names (same logic as inferMyTeam)
  const normalizeTeamName = (teamName) => {
    if (!teamName) return teamName;
    return teamName
      .replace(/\s+\d+\s+\w+$/i, '') // Remove "16 Black", "2024 Gold", etc.
      .replace(/\s+\d+$/i, '') // Remove trailing numbers like "2024"
      .trim();
  };

  dlog('📊 WIN/LOSS DEBUG - Processing matches for team:', myTeam);
  
  matches.forEach(match => {
    const teamAName = typeof match.teamA === 'object' ? match.teamA.name || match.teamA.teamName : match.teamA;
    const teamBName = typeof match.teamB === 'object' ? match.teamB.name || match.teamB.teamName : match.teamB;
    
    const normalizedTeamA = normalizeTeamName(teamAName);
    const normalizedTeamB = normalizeTeamName(teamBName);
    
    if (normalizedTeamA === myTeam || normalizedTeamB === myTeam) {
      totalGames++;
      const isTeamA = normalizedTeamA === myTeam;
      
      // For volleyball, we need to check sets won, not total points
      // Check if match has sets data
      if (match.sets && Array.isArray(match.sets)) {
        let teamASetsWon = 0;
        let teamBSetsWon = 0;
        
        match.sets.forEach(set => {
          const aScore = parseInt(set.teamA) || 0;
          const bScore = parseInt(set.teamB) || 0;
          
          dlog(`    Set: A=${aScore} vs B=${bScore}`);
          
          if (aScore > bScore) {
            teamASetsWon++;
            dlog(`    → Team A wins this set`);
          } else if (bScore > aScore) {
            teamBSetsWon++;
            dlog(`    → Team B wins this set`);
          } else {
            dlog(`    → Tie set`);
          }
        });
        
        dlog('🏐 Sets Analysis:', {
          matchId: match.id,
          teamASetsWon,
          teamBSetsWon,
          isTeamA,
          myTeam
        });
        
        // Determine winner based on sets won (adaptive volleyball rules)
        dlog(`🔍 About to call checkMatchCompletion for match ${match.id}:`, {
          teamASetsWon,
          teamBSetsWon,
          matchSets: match.sets
        });
        const { isComplete, winner } = checkMatchCompletion({ teamA: teamASetsWon, teamB: teamBSetsWon }, match.sets);
        dlog(`🔍 checkMatchCompletion returned for match ${match.id}:`, { isComplete, winner });
        
        dlog(`🏆 Match ${match.id} completion check:`, {
          teamASetsWon,
          teamBSetsWon,
          isComplete,
          winner,
          isTeamA,
          myTeam,
          willCountAsWin: isComplete && ((winner === 'teamA' && isTeamA) || (winner === 'teamB' && !isTeamA))
        });
        
        if (isComplete) {
          if ((winner === 'teamA' && isTeamA) || (winner === 'teamB' && !isTeamA)) {
            wins++;
            dlog(`✅ Match ${match.id} counted as WIN for ${myTeam}`);
          } else {
            dlog(`❌ Match ${match.id} counted as LOSS for ${myTeam}`);
          }
        } else {
          dlog(`⏸️ Match ${match.id} not complete, not counting`);
        }
      } else {
        // Fallback to old logic if no sets data (for backward compatibility)
        const teamAScore = match.teamAScore || 0;
        const teamBScore = match.teamBScore || 0;
        
        if ((isTeamA && teamAScore > teamBScore) || (!isTeamA && teamBScore > teamAScore)) {
          wins++;
        }
      }
    }
  });

  if (totalGames === 0) {
    return { emotion: EMOTIONS.CALM, stats: { myTeam, wins: 0, losses: 0, totalGames: 0, winRate: 0 } };
  }

  const winRate = wins / totalGames;

  dlog('📊 Win/Loss Analysis:', { myTeam, wins, totalGames, winRate });

  // Calculate the emotion using the same logic
  let emotion;
  // Progressive emotional range expansion based on match count
  if (totalGames < EMOTION_THRESHOLDS.MIN_MATCHES_BASIC) {
    // 1-2 matches: Very limited range (only extreme states)
    dlog('📊 Using minimal emotional range (1-2 matches)');
    if (winRate >= 0.5) {
      emotion = EMOTIONS.JOYFUL; // Any wins = joyful
    } else {
      emotion = EMOTIONS.COMPETITIVE; // Any losses = competitive/tense
    }
  } else if (totalGames < EMOTION_THRESHOLDS.MIN_MATCHES_NUANCED) {
    // Basic range (3-7 matches): Joyful, Competitive only
    dlog('📊 Using basic emotional range (3-7 matches)');
    if (winRate >= 0.6) {
      emotion = EMOTIONS.JOYFUL; // Mostly winning
    } else if (winRate >= 0.4) {
      emotion = EMOTIONS.CALM; // Even record
    } else {
      emotion = EMOTIONS.COMPETITIVE; // Mostly losing (tense/frustrated)
    }
  } else if (totalGames < EMOTION_THRESHOLDS.MIN_MATCHES_FULL) {
    // Expanded range (8-14 matches): + Resilient
    dlog('📊 Using expanded emotional range (8-14 matches)');
    if (winRate >= 0.7) {
      emotion = EMOTIONS.JOYFUL; // Dominant winning
    } else if (winRate >= 0.5) {
      emotion = EMOTIONS.CALM; // Even record
    } else if (winRate >= 0.3) {
      emotion = EMOTIONS.RESILIENT; // Struggling but showing resilience
    } else {
      emotion = EMOTIONS.COMPETITIVE; // Mostly losing (tense/frustrated)
    }
  } else {
    // Full emotional range (15+ matches): All emotions available
    dlog('📊 Using full emotional range (15+ matches)');
    if (winRate >= 0.7) {
      emotion = EMOTIONS.JOYFUL; // Dominant winning
    } else if (winRate >= 0.5) {
      emotion = EMOTIONS.CALM; // Even record
    } else if (winRate >= 0.3) {
      emotion = EMOTIONS.RESILIENT; // Struggling but resilient
    } else {
      emotion = EMOTIONS.REFLECTIVE; // Consistently losing (withdrawing/reflective)
    }
  }

  const stats = { 
    myTeam, 
    wins, 
    losses: totalGames - wins, 
    totalGames, 
    winRate: Math.round(winRate * 100) 
  };

  return { emotion, stats };
}

/**
 * Calculate performance emotion based on win/loss patterns
 * Infers "my team" based on most frequently used team name
 */
export function calculatePerformanceEmotion(matches) {
  if (!matches || matches.length === 0) {
    return EMOTIONS.CALM;
  }

  // Debug: Show what team data looks like
  dlog('🔍 Team Data Debug:', matches.map(m => ({ 
    teamA: m.teamA, 
    teamB: m.teamB, 
    teamAType: typeof m.teamA,
    teamBType: typeof m.teamB,
    teamAColor: m.teamAColor || (m.teamA && m.teamA.color),
    teamBColor: m.teamBColor || (m.teamB && m.teamB.color),
    score: m.score,
    setsWon: m.setsWon,
    sets: m.sets,
    hasSets: !!m.sets
  })));

  // Infer "my team" based on most frequently used team name
  const myTeam = inferMyTeam(matches);
  dlog('🏆 My Team Inference:', { myTeam, totalMatches: matches.length });
  if (!myTeam) {
    return EMOTIONS.CALM;
  }

  // Calculate win/loss ratio for my team (volleyball rules - by sets won)
  let wins = 0;
  let totalGames = 0;

  // Helper to normalize team names (same logic as inferMyTeam)
  const normalizeTeamName = (teamName) => {
    if (!teamName) return teamName;
    return teamName
      .replace(/\s+\d+\s+\w+$/i, '') // Remove "16 Black", "2024 Gold", etc.
      .replace(/\s+\d+$/i, '') // Remove trailing numbers like "2024"
      .trim();
  };

  matches.forEach(match => {
    const teamAName = typeof match.teamA === 'object' ? match.teamA.name || match.teamA.teamName : match.teamA;
    const teamBName = typeof match.teamB === 'object' ? match.teamB.name || match.teamB.teamName : match.teamB;
    
    const normalizedTeamA = normalizeTeamName(teamAName);
    const normalizedTeamB = normalizeTeamName(teamBName);
    
    if (normalizedTeamA === myTeam || normalizedTeamB === myTeam) {
      totalGames++;
      const isTeamA = normalizedTeamA === myTeam;
      
      // For volleyball, we need to check sets won, not total points
      // Check if match has sets data
      if (match.sets && Array.isArray(match.sets)) {
        // Calculate sets won for each team
        let teamASetsWon = 0;
        let teamBSetsWon = 0;
        
        match.sets.forEach(set => {
          // Sets are stored as { teamA: score, teamB: score }
          // BUT: The scores shown in UI are POINTS within each set, not sets won
          // We need to determine who won each set by comparing points
          const setAScore = set.teamA || 0;
          const setBScore = set.teamB || 0;
          
          dlog(`    Set: A=${setAScore} vs B=${setBScore}`);
          
          if (setAScore > setBScore) {
            teamASetsWon++;
            dlog(`    → Team A wins this set`);
          } else if (setBScore > setAScore) {
            teamBSetsWon++;
            dlog(`    → Team B wins this set`);
          } else {
            dlog(`    → Tie set`);
          }
        });
        
        dlog('🏐 Sets Analysis:', {
          matchId: match.id,
          teamASetsWon,
          teamBSetsWon,
          isTeamA,
          myTeam,
          setsCount: match.sets.length,
          setsDetails: match.sets.map(s => ({
            teamAScore: s.teamA,
            teamBScore: s.teamB,
            winner: s.teamA > s.teamB ? 'A' : (s.teamB > s.teamA ? 'B' : 'tie')
          }))
        });
        
        // Determine winner based on sets won (adaptive volleyball rules)
        const { isComplete, winner } = checkMatchCompletion({ teamA: teamASetsWon, teamB: teamBSetsWon }, match.sets);
        
        if (isComplete) {
          if ((winner === 'teamA' && isTeamA) || (winner === 'teamB' && !isTeamA)) {
            wins++;
          }
        }
      } else {
        // Fallback to old logic if no sets data (for backward compatibility)
        const teamAScore = match.teamAScore || 0;
        const teamBScore = match.teamBScore || 0;
        
        if ((isTeamA && teamAScore > teamBScore) || (!isTeamA && teamBScore > teamAScore)) {
          wins++;
        }
      }
    }
  });

  if (totalGames === 0) {
    return EMOTIONS.CALM;
  }

  const winRate = wins / totalGames;

  dlog('📊 Win/Loss Analysis:', { myTeam, wins, totalGames, winRate });

  // Progressive emotional range expansion based on match count
  if (totalGames < EMOTION_THRESHOLDS.MIN_MATCHES_BASIC) {
    // 1-2 matches: Very limited range (only extreme states)
    dlog('📊 Using minimal emotional range (1-2 matches)');
    if (winRate >= 0.5) {
      return EMOTIONS.JOYFUL; // Any wins = joyful
    } else {
      return EMOTIONS.COMPETITIVE; // Any losses = competitive/tense
    }
  }

  // Basic range (3-7 matches): Joyful, Competitive only
  if (totalGames < EMOTION_THRESHOLDS.MIN_MATCHES_NUANCED) {
    dlog('📊 Using basic emotional range (3-7 matches)');
    if (winRate >= 0.6) {
      return EMOTIONS.JOYFUL; // Mostly winning
    } else if (winRate >= 0.4) {
      return EMOTIONS.CALM; // Even record
    } else {
      return EMOTIONS.COMPETITIVE; // Mostly losing (tense/frustrated)
    }
  }

  // Expanded range (8-14 matches): + Resilient
  if (totalGames < EMOTION_THRESHOLDS.MIN_MATCHES_FULL) {
    dlog('📊 Using expanded emotional range (8-14 matches)');
    if (winRate >= 0.7) {
      return EMOTIONS.JOYFUL; // Dominant winning
    } else if (winRate >= 0.5) {
      return EMOTIONS.CALM; // Even record
    } else if (winRate >= 0.3) {
      return EMOTIONS.RESILIENT; // Struggling but showing resilience
    } else {
      return EMOTIONS.COMPETITIVE; // Mostly losing (tense/frustrated)
    }
  }

  // Full emotional range (15+ matches): All emotions available
  dlog('📊 Using full emotional range (15+ matches)');
  if (winRate >= 0.7) {
    return EMOTIONS.JOYFUL; // Dominant winning
  } else if (winRate >= 0.5) {
    return EMOTIONS.CALM; // Even record
  } else if (winRate >= 0.3) {
    return EMOTIONS.RESILIENT; // Struggling but resilient
  } else {
    return EMOTIONS.REFLECTIVE; // Consistently losing (withdrawing/reflective)
  }
}

/**
 * Infer which team is "my team" based on frequency of team names
 * For speech bubble stats, prioritizes team name over color variations
 * This allows different parents scoring same team with different colors
 * to still be counted as the same team for win/loss statistics
 */
export function inferMyTeam(matches, thresholdType = 'logo') {
  if (!matches || matches.length === 0) {
    return null;
  }

  const teamCounts = {};
  const teamDates = {}; // Track most recent date for each team
  
  // Helper to check if a team name is a placeholder
  const isPlaceholderTeam = (teamName) => {
    // Explicitly ban common placeholder teams
    const bannedTeams = ['Team A', 'Team B', 'Team 1', 'Team 2', 'Team C', 'Team D'];
    return bannedTeams.includes(teamName) || 
           /^Team [A-Z]$/i.test(teamName) || 
           /^Team [0-9]+$/i.test(teamName);
  };
  
  // Helper to normalize team names (group similar names)
  const normalizeTeamName = (teamName) => {
    if (!teamName) return teamName;
    
    // Remove common suffixes that don't change the core team identity
    // e.g., "Tyrants 16 Black" -> "Tyrants", "Team Red 2024" -> "Team Red"
    return teamName
      .replace(/\s+\d+\s+\w+$/i, '') // Remove "16 Black", "2024 Gold", etc.
      .replace(/\s+\d+$/i, '') // Remove trailing numbers like "2024"
      .trim();
  };
  
  matches.forEach(match => {
    const matchDate = match.createdAt || match.updatedAt || Date.now();
    
    if (match.teamA) {
      const teamAName = typeof match.teamA === 'object' ? match.teamA.name || match.teamA.teamName : match.teamA;
      const normalizedName = normalizeTeamName(teamAName);
      
      // Only count non-placeholder teams
      if (normalizedName && !isPlaceholderTeam(normalizedName)) {
        teamCounts[normalizedName] = (teamCounts[normalizedName] || 0) + 1;
        if (!teamDates[normalizedName] || matchDate > teamDates[normalizedName]) {
          teamDates[normalizedName] = matchDate;
        }
      } else if (normalizedName) {
        dlog(`🚫 Filtered out placeholder team A: "${normalizedName}"`);
      }
    }
    if (match.teamB) {
      const teamBName = typeof match.teamB === 'object' ? match.teamB.name || match.teamB.teamName : match.teamB;
      const normalizedName = normalizeTeamName(teamBName);
      
      // Only count non-placeholder teams
      if (normalizedName && !isPlaceholderTeam(normalizedName)) {
        teamCounts[normalizedName] = (teamCounts[normalizedName] || 0) + 1;
        if (!teamDates[normalizedName] || matchDate > teamDates[normalizedName]) {
          teamDates[normalizedName] = matchDate;
        }
      } else if (normalizedName) {
        dlog(`🚫 Filtered out placeholder team B: "${normalizedName}"`);
      }
    }
  });

  dlog('👥 Team counts (normalized by name):', teamCounts);
  dlog('📅 Team dates:', Object.entries(teamDates).map(([team, date]) => ({ team, date: new Date(date).toISOString() })));
  
  // Debug: Show which teams are being considered
  const validTeams = Object.keys(teamCounts);
  dlog('🎯 Valid teams after filtering:', validTeams);
  dlog('🚫 Teams that should be banned:', ['Team A', 'Team B', 'Team 1', 'Team 2', 'Team C', 'Team D']);

  // If no teams found, return null
  if (Object.keys(teamCounts).length === 0) {
    return null;
  }

  // Different thresholds for different use cases
  const totalMatches = matches.length;
  let threshold;
  
  if (thresholdType === 'settings') {
    // Settings: More conservative (stable defaults)
    threshold = Math.max(4, totalMatches * 0.5);
  } else {
    // Logo: More aggressive (instant gratification)
    threshold = Math.max(2, totalMatches * 0.4);
  }

  for (const [teamName, count] of Object.entries(teamCounts)) {
    if (count > threshold) {
      dlog(`🎯 Selected team (${thresholdType} threshold: ${threshold}):`, teamName, 'with', count, 'matches');
      return teamName;
    }
  }

  // If no team appears more than 50% of the time, return the most frequent
  // Use recency as tiebreaker
  const entries = Object.entries(teamCounts);
  if (entries.length === 0) {
    return null;
  }

  const mostFrequent = entries.reduce((a, b) => {
    const countA = teamCounts[a[0]];
    const countB = teamCounts[b[0]];
    
    // If counts are equal, use most recent date as tiebreaker
    if (countA === countB) {
      const dateA = teamDates[a[0]] || 0;
      const dateB = teamDates[b[0]] || 0;
      return dateA > dateB ? a : b;
    }
    
    // Otherwise, return team with higher count
    return countA > countB ? a : b;
  }, entries[0]); // Use first entry as initial value

  dlog('🎯 Selected team (most frequent):', mostFrequent[0], 'with', teamCounts[mostFrequent[0]], 'matches');
  return mostFrequent ? mostFrequent[0] : null;
}

/**
 * Get winning team color from matches
 */
export function getWinningTeamColor(matches) {
  if (!matches || matches.length === 0) {
    return '#C4B5A0'; // Default beige
  }

  // TODO: Implement logic to determine which team won most matches
  // For now, return a demo color
  return '#FF6B35'; // Demo orange color
}

/**
 * Blend a base color with an emotion's color palette
 * Enhanced to work with the color clustering system
 */
export function blendColorWithEmotion(baseColor, emotion, intensity = 0.5) {
  const emotionColor = getEmotionColor(emotion);
  
  // Parse HSL values for proper blending
  const parseHsl = (hslString) => {
    const match = hslString.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (match) {
      return {
        h: parseInt(match[1]),
        s: parseInt(match[2]),
        l: parseInt(match[3])
      };
    }
    return null;
  };
  
  const baseHsl = parseHsl(baseColor);
  const emotionHsl = parseHsl(emotionColor);
  
  if (baseHsl && emotionHsl) {
    // Blend the colors based on intensity
    const blendedHsl = {
      h: Math.round(baseHsl.h * (1 - intensity) + emotionHsl.h * intensity),
      s: Math.round(baseHsl.s * (1 - intensity) + emotionHsl.s * intensity),
      l: Math.round(baseHsl.l * (1 - intensity) + emotionHsl.l * intensity)
    };
    
    return `hsl(${blendedHsl.h}, ${blendedHsl.s}%, ${blendedHsl.l}%)`;
  }
  
  // Fallback to emotion color if parsing fails
  return emotionColor;
}

/**
 * Generate explanation text for the logo tooltip
 * @param {Array} matches - Array of match data
 * @returns {String} - Explanation text for the tooltip
 */
export function getEmotionExplanation(matches) {
  if (!matches || matches.length === 0) {
    return "Hi!\nReady to play";
  }

  // Use the same logic as the main emotion engine to get both emotion and stats
  const { emotion: performanceEmotion, stats } = calculatePerformanceEmotionWithStats(matches);
  const { myTeam, wins, losses, totalGames, winRate } = stats;
  
  dlog('🎯 SPEECH BUBBLE DEBUG - Raw stats from emotion engine:', {
    myTeam,
    wins,
    losses, 
    totalGames,
    winRate,
    performanceEmotion,
    matchesCount: matches.length
  });
  
  dlog('🎯 getEmotionExplanation using emotion engine stats:', { 
    myTeam, 
    wins, 
    losses, 
    totalGames, 
    winRate,
    performanceEmotion,
    matchesCount: matches.length,
    matchesData: matches.map(m => ({
      id: m.id,
      teamA: m.teamA?.name,
      teamB: m.teamB?.name,
      setsWon: m.setsWon,
      sets: m.sets?.length,
      status: m.status
    }))
  });

  let explanation = "";
  
  const prominentTeam = myTeam || (matches.length > 0 ? "my team" : null);
  
  if (totalGames === 0) {
    if (matches.length === 0) {
      explanation = "Hi!\nReady to play";
    } else {
      explanation = `${matches.length} matches tracked\nLearning teams`;
    }
  } else {
    const teamName = prominentTeam;
    
    // Check if this is a placeholder team (Team A, Team B, etc.)
    const isPlaceholder = /^Team [A-Z]$/i.test(teamName);
    
    // If placeholder, show neutral stats instead of emotive messages
    if (isPlaceholder) {
      if (winRate === 100) {
        explanation = `${teamName} ${wins}-0\nUndefeated`;
      } else if (winRate === 0) {
        explanation = `${teamName} 0-${losses}\nNo wins`;
      } else {
        explanation = `${teamName} ${wins}-${losses}\n${winRate}% wins`;
      }
      return explanation;
    }
    
    // Message variations based on emotion and win rate
    switch (performanceEmotion) {
      case EMOTIONS.JOYFUL:
        if (winRate === 100) {
          const perfectMessages = [
            `${teamName}\n${wins}-0 undefeated`,
            `${teamName} ${wins}-0\nPerfect record`,
            `${teamName}\n${wins} wins no losses`
          ];
          explanation = perfectMessages[Math.floor(Math.random() * perfectMessages.length)];
        } else if (winRate >= 80) {
          const highJoyMessages = [
            `${teamName} ${wins}-${losses}\n${winRate}% wins`,
            `${teamName} ${wins}-${losses}\n${winRate}% record`,
            `${teamName} ${wins}-${losses}\n${winRate}%`
          ];
          explanation = highJoyMessages[Math.floor(Math.random() * highJoyMessages.length)];
        } else {
          const joyMessages = [
            `${teamName} ${wins}-${losses}\n${winRate}% wins`,
            `${teamName} ${wins}-${losses}\n${winRate}% record`,
            `${teamName} ${wins}-${losses}\n${winRate}%`
          ];
          explanation = joyMessages[Math.floor(Math.random() * joyMessages.length)];
        }
        break;
        
      case EMOTIONS.COMPETITIVE:
        if (totalGames < 3) {
          const earlyCompMessages = [
            `${teamName}\n${wins}-${losses}`,
            `${teamName} ${wins}-${losses}\nTied up`,
            `${teamName}\n${wins}W ${losses}L`
          ];
          explanation = earlyCompMessages[Math.floor(Math.random() * earlyCompMessages.length)];
        } else {
          const compMessages = [
            `${teamName} ${wins}-${losses}\n${winRate}%`,
            `${teamName}\n${winRate}% record`,
            `${teamName} ${wins}-${losses}\n${winRate}% wins`
          ];
          explanation = compMessages[Math.floor(Math.random() * compMessages.length)];
        }
        break;
        
      case EMOTIONS.RESILIENT:
        const resilientMessages = [
          `${teamName} ${wins}-${losses}\n${winRate}%`,
          `${teamName}\n${winRate}% record`,
          `${teamName} ${wins}-${losses}\n${winRate}% wins`
        ];
        explanation = resilientMessages[Math.floor(Math.random() * resilientMessages.length)];
        break;
        
      case EMOTIONS.REFLECTIVE:
        if (winRate === 0) {
          const reflectiveZeroMessages = [
            `${teamName} 0-${losses}\nNo wins`,
            `${teamName}\n0-${losses} record`,
            `${teamName} 0-${losses}\nSearching`
          ];
          explanation = reflectiveZeroMessages[Math.floor(Math.random() * reflectiveZeroMessages.length)];
        } else {
          const reflectiveMessages = [
            `${teamName} ${wins}-${losses}\n${winRate}%`,
            `${teamName}\n${winRate}% record`,
            `${teamName} ${wins}-${losses}\n${winRate}% wins`
          ];
          explanation = reflectiveMessages[Math.floor(Math.random() * reflectiveMessages.length)];
        }
        break;
        
      case EMOTIONS.CALM:
        const calmMessages = [
          `${teamName} ${wins}-${losses}\nEven split`,
          `${teamName}\n${winRate}% record`,
          `${teamName} ${wins}-${losses}\n${winRate}%`
        ];
        explanation = calmMessages[Math.floor(Math.random() * calmMessages.length)];
        break;
        
      default:
        explanation = `${teamName} ${wins}-${losses}\n${winRate}%`;
    }
  }

  return explanation;
}
